import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import IconButton from '../components/IconButton';
import { Button, Table, TableRow } from '../components/ui';
import ServiceForm from '../components/ServiceForm';
import { useAuth } from '../context/AuthContext';

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const hasPermission = (moduleName, action) => {
    const perms = user?.permissions || {};
    const key = Object.keys(perms).find(k => k.toLowerCase() === moduleName.toLowerCase());
    return !!(key && perms[key] && perms[key][action]);
  };

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const { data } = await api.get('/services');
      setServices(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (formData) => {
    try {
      setSuccess('');
      await api.post('/services', formData);
      await loadServices();
      setIsModalOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create service');
    }
  };

  const handleUpdate = async (formData) => {
    try {
      setSuccess('');
      await api.put(`/services/${editingService.id}`, formData);
      await loadServices();
      setEditingService(null);
      setIsModalOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update service');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }
    try {
      setError('');
      await api.delete(`/services/${id}`);
      setSuccess('Service deleted successfully');
      await loadServices();
    } catch (err) {
      setSuccess('');
      setError(err.response?.data?.error || 'Failed to delete service');
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingService(null);
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="services-page">
      <div className="modern-header">
        <div className="header-left">
          <button 
            onClick={() => navigate(-1)}
            className="back-button"
            title="Go back"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back
          </button>
        </div>

        <div className="header-center">
          <h2>Services</h2>
        </div>

        <div className="header-right">
          {hasPermission('Service', 'create') && (
            <>
              <input
                type="file"
                accept=".xlsx,.xls"
                id="service-upload-input"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const file = e.target.files && e.target.files[0];
                  if (!file) return;
                  setError('');
                  setSuccess('');
                  setLoading(true);
                  try {
                    const form = new FormData();
                    form.append('file', file);
                    const res = await api.post('/services/bulk-upload', form);
                    const created = res.data?.created || 0;
                    const errors = res.data?.errors || [];
                    if (errors.length) {
                      setError(`Created ${created}. ${errors.length} rows had errors.`);
                    } else {
                      setSuccess('Bulk upload successful. Created ' + created + ' services.');
                    }
                    await loadServices();
                  } catch (err) {
                    setError(err.response?.data?.error || err.message || 'Bulk upload failed');
                  } finally {
                    setLoading(false);
                    e.target.value = '';
                  }
                }}
              />
              <Button 
                onClick={() => document.getElementById('service-upload-input').click()}
                className="upload-button"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
                </svg>
                Upload
              </Button>
              <Button 
                onClick={() => setIsModalOpen(true)}
                className="add-button"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 1V15M1 8H15" stroke="currentColor" strokeWidth="2"/>
                </svg>
                Service
              </Button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      {success && (
        <div className="success-banner">
          {success}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingService ? 'Edit' : 'New'} Service</h3>
              <button className="close-button" onClick={handleCloseModal}>×</button>
            </div>
            <ServiceForm
              service={editingService}
              onSubmit={editingService ? handleUpdate : handleCreate}
              onCancel={handleCloseModal}
            />
          </div>
        </div>
      )}

      <div className="services-container table-responsive">
        {/* <Table headers={['Name', 'Type', 'Actions']}> */}
        <Table headers={['Name', 'Actions']}>
          {services.map(service => (
            <TableRow key={service.id}>
              <td>{service.name}</td>
              {/* <td>{service.type}</td> */}
              <td>
                <div style={{ display: 'flex', gap: 8 }}>
                  {hasPermission('Service', 'update') && (
                    <IconButton title="Edit service" onClick={() => handleEdit(service)}>
                      <svg viewBox="0 0 24 24" fill="none"><path d="M3 21v-3.6l11-11L20.6 9 9.6 20H3z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </IconButton>
                  )}
                  {hasPermission('Service', 'delete') && (
                    <IconButton title="Delete service" onClick={() => handleDelete(service.id)}>
                      <svg viewBox="0 0 24 24" fill="none"><path d="M3 6h18" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M8 6v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </IconButton>
                  )}
                </div>
              </td>
            </TableRow>
          ))}
        </Table>
      </div>

      <style jsx>{`
        .services-page {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
        }

        .modern-header {
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
          border-bottom: 1px solid #e1e4e8;
          margin: -20px -20px 20px;
          padding: 20px 24px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-radius: 12px 12px 0 0;
        }

        .header-left {
          display: flex;
          align-items: center;
        }

        .back-button {
          display: flex;
          align-items: center;
          gap: 6px;
          background-color: #6c757d;
          color: white;
          border: none;
          padding: 10px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          font-size: 14px;
          transition: all 200ms ease;
        }

        .back-button:hover {
          background-color: #5a6268;
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
        }

        .back-button:active {
          transform: translateY(0);
        }

        .header-center {
          flex: 1;
          text-align: center;
        }

        .header-center h2 {
          margin: 0;
          color: #172B4D;
          font-size: 28px;
          font-weight: 700;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .upload-button {
          background-color: #ff9f1c !important;
          color: white !important;
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px !important;
          border-radius: 6px !important;
          font-weight: 600 !important;
          font-size: 14px !important;
          transition: all 200ms ease !important;
          border: none !important;
          cursor: pointer !important;
        }

        .upload-button:hover {
          background-color: #e88d1f !important;
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1) !important;
        }

        .add-button {
          background-color: #00b359 !important;
          color: white !important;
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px !important;
          border-radius: 6px !important;
          font-weight: 600 !important;
          font-size: 14px !important;
          transition: all 200ms ease !important;
          border: none !important;
          cursor: pointer !important;
        }

        .add-button:hover {
          background-color: #008c3a !important;
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1) !important;
        }

        .header {
          background: #fff;
          border-bottom: 1px solid #eee;
          margin: -20px -20px 20px;
          padding: 20px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .header h2 {
          margin: 0;
          color: #172B4D;
          font-size: 24px;
        }

        .error-banner {
          background: #FFEBE6;
          color: #DE350B;
          padding: 12px 16px;
          border-radius: 3px;
          margin-bottom: 20px;
          text-align: center;
        }

        .success-banner {
          background: #DFFCF0;
          color: #216E4E;
          padding: 12px 16px;
          border-radius: 3px;
          margin-bottom: 20px;
          text-align: center;
        }

        .services-container {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
          padding: 16px;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(9, 30, 66, 0.54);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          width: 100%;
          max-width: 500px;
          padding: 24px;
          position: relative;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 20px;
          color: #172B4D;
        }

        .close-button {
          background: none;
          border: none;
          font-size: 24px;
          color: #6B778C;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 3px;
        }

        .close-button:hover {
          background: #F4F5F7;
        }

        @media (max-width: 768px) {
          .modern-header {
            flex-direction: column;
            gap: 16px;
          }

          .header-left,
          .header-right {
            width: 100%;
            justify-content: center;
          }

          .header-center {
            order: -1;
            width: 100%;
          }

          .header-center h2 {
            font-size: 22px;
          }

          .back-button,
          .upload-button,
          .add-button {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}